import { parseProjectFile, type Project, type ProjectFile } from '@rcs/data-schema';
import { toProjectFile } from '../project/file';

/** プロジェクトの保存先。 */
export interface ProjectStore {
  /** 保存してあるデータ（検証前）。なければ `undefined` */
  load(): Promise<unknown>;
  /** 保存する。途中で終了しても、直前の保存済みデータは壊れない（NFR-REL-001） */
  save(file: ProjectFile): Promise<void>;
}

const DB_NAME = 'rcs';
const STORE_NAME = 'projects';
const CURRENT_KEY = 'current';

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB を開けません'));
    };
  });
}

/**
 * IndexedDB の保存先。1 回の保存を 1 つのトランザクションで書く
 * （コミットされるまで既存のデータはそのまま残り、途中終了では書きかけにならない）。
 */
export function createIndexedDbStore(factory: IDBFactory = indexedDB): ProjectStore {
  const run = async <T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> => {
    const db = await openDatabase(factory);
    try {
      return await new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode);
        const request = action(transaction.objectStore(STORE_NAME));
        // 書き込みは、トランザクションが完了（コミット）してから成功とする
        transaction.oncomplete = () => {
          resolve(request.result);
        };
        transaction.onerror = () => {
          reject(transaction.error ?? new Error('IndexedDB の処理に失敗しました'));
        };
        transaction.onabort = () => {
          reject(transaction.error ?? new Error('IndexedDB の処理が中断されました'));
        };
      });
    } finally {
      db.close();
    }
  };
  return {
    load: () => run<unknown>('readonly', (store) => store.get(CURRENT_KEY)),
    save: async (file) => {
      await run('readwrite', (store) => store.put(file, CURRENT_KEY));
    },
  };
}

/** 起動時の復元の結果。 */
export type RestoreResult =
  | { readonly status: 'restored'; readonly project: Project }
  | { readonly status: 'empty' }
  | { readonly status: 'failed'; readonly message: string };

/**
 * 前回のプロジェクトを復元する。保存データがなければ `empty`。読み込めない（壊れている・新しすぎる）
 * ときは `failed` で理由を返し、保存データは書き換えない。
 */
export async function restoreProject(store: ProjectStore): Promise<RestoreResult> {
  let stored: unknown;
  try {
    stored = await store.load();
  } catch (error) {
    return { status: 'failed', message: error instanceof Error ? error.message : String(error) };
  }
  if (stored === undefined) return { status: 'empty' };
  const parsed = parseProjectFile(stored);
  if (!parsed.ok) return { status: 'failed', message: parsed.error.message };
  return { status: 'restored', project: parsed.value.project };
}

/** プロジェクトを保存する。 */
export function saveProject(store: ProjectStore, project: Project): Promise<void> {
  return store.save(toProjectFile(project));
}

/**
 * 永続ストレージを要求する（NFR-REL-002）。ブラウザによる自動削除を避けるため。
 * 使えない環境・拒否された場合は `false`（保存自体はできる）。
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !('storage' in navigator)) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
